import { fetchTasksResult } from './connector-next-components';

// The connector-next fetcher parses a JSON response regardless of the caller's
// declared return type, so a result file served as application/json (for
// example optimization_objectives.json) arrives as a parsed object. Passing
// that object to `new File([obj], ...)` stringifies it to "[object Object]",
// which breaks JSON.parse downstream. Re-serialize it so the File carries the
// real bytes.
function toBlob(value: unknown): Blob {
    if (value instanceof Blob) {
        return value;
    }
    return new Blob([JSON.stringify(value)], {
        type: 'application/json',
    });
}

export type DownloadOutcome = {
    files: File[];
    errors: string[];
};

/**
 * Downloads the named result files of a completed task. Each file is fetched
 * independently, so one failure does not discard the files that did arrive;
 * the failures are returned as messages next to them.
 *
 * The files are re-identified to the original local patient by the connector.
 *
 * @param taskId - Connector task id.
 * @param resultPaths - Result file names, as reported on the task view.
 */
export const downloadTaskResults = async (
    taskId: string,
    resultPaths: string[]
): Promise<DownloadOutcome> => {
    const outcomes = await Promise.allSettled(
        resultPaths.map(async (resultPath) => {
            const res = await fetchTasksResult({
                pathParams: { id: taskId, resultPath },
            });
            return new File([toBlob(res)], resultPath);
        })
    );

    const files: File[] = [];
    const errors: string[] = [];

    outcomes.forEach((outcome, i) => {
        if (outcome.status === 'fulfilled') {
            files.push(outcome.value);
        } else {
            errors.push(
                `Failed to download result file ${resultPaths[i]} ` +
                    `for task ${taskId}: ${outcome.reason?.message || outcome.reason}`
            );
        }
    });

    return { files, errors };
};
